import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ClipboardList, Check, X, UserPlus, Banknote } from "lucide-react";
import { Attachments } from "@/components/Attachments";

const TONE = { pending: "secondary", accepted: "default", rejected: "destructive", withdrawn: "outline" };

export default function FieldSubmissions() {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [accept, setAccept] = useState(null);
  const [reject, setReject] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/drafts?status=${status}`).then((r) => setData(r.data)), [status]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)); }, []);

  const doAccept = async () => {
    setBusy(true);
    try {
      const body = { credit_days: accept.credit_days === "" ? null : Number(accept.credit_days) };
      if (accept.map_to && accept.map_to !== "new") body.customer_id = accept.map_to;
      const { data: r } = await api.post(`/admin/drafts/${accept.d.id}/accept`, body);
      toast.success(`Invoice ${r.order.invoice_no} created${r.order.approval_status === "pending" ? " - awaiting discount approval" : ""}`);
      setAccept(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const doReject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/drafts/${reject.d.id}/reject`, { reason: reject.reason });
      toast.success("Submission rejected - agent notified");
      setReject(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Field Submissions" subtitle="Sales submitted by agents from the field. Accept to create the invoice (discounts above 5% still go to approval) or reject with a reason." />
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending", "accepted", "rejected", "withdrawn", "all"].map((f) => <Button key={f} size="sm" variant={status === f ? "default" : "outline"} className="capitalize" onClick={() => setStatus(f)} data-testid={`draft-filter-${f}`}>{f}{f === "pending" && data?.pending ? ` (${data.pending})` : ""}</Button>)}
      </div>
      {!data ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : data.items.length === 0 ? <Empty title="Nothing here" subtitle="Agents submit sales from the Submit tab of their app." />
          : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {data.items.map((d) => (
                <Card key={d.id} className="p-5" data-testid={`draft-card-${d.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display font-semibold flex items-center gap-2">{d.customer_name}{d.new_customer && <Badge variant="outline" className="gap-1"><UserPlus className="w-3 h-3" /> new customer</Badge>}</p>
                      <p className="text-xs text-muted-foreground">by {d.agent} · {d.date} · submitted {d.submitted_at?.slice(0, 16).replace("T", " ")}</p>
                    </div>
                    <Badge variant={TONE[d.status]} className="capitalize">{d.status}</Badge>
                  </div>
                  <table className="w-full text-sm mt-3">
                    <tbody>
                      {d.lines.map((l, i) => (
                        <tr key={i} className="border-b border-border/40">
                          <td className="py-1.5">{l.product_name || l.product_id}</td>
                          <td className="py-1.5 text-right text-muted-foreground">{l.qty} × {l.discount_level}%{l.above_list_price_per_bag > 0 ? ` +${l.above_list_price_per_bag}` : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm">
                    <span>{d.estimate.bags} bags · <b>{money(d.estimate.sales)}</b> · comm. {money(d.estimate.commission)}</span>
                    <span className="flex gap-1.5">
                      {d.cod_paid && <Badge className="gap-1"><Banknote className="w-3 h-3" /> COD paid</Badge>}
                      {d.needs_approval && <Badge variant="secondary">{d.estimate.max_discount}% needs approval</Badge>}
                    </span>
                  </div>
                  {d.note && <p className="text-xs text-muted-foreground mt-2 rounded bg-secondary/50 p-2">"{d.note}"</p>}
                  {d.status === "pending" && <div className="mt-3"><Attachments entityType="draft" entityId={d.id} canUpload={false} compact /></div>}
                  {d.status === "rejected" && d.reason && <p className="text-xs text-destructive mt-2">Reason: {d.reason}</p>}
                  {d.status === "accepted" && d.order_id && <Link to={`/invoice/${d.order_id}`} className="text-xs text-primary hover:underline mt-2 inline-block">Invoice {d.invoice_no}</Link>}
                  {d.status === "pending" && (
                    <div className="flex justify-end gap-2 mt-4">
                      <Button size="sm" variant="outline" onClick={() => setReject({ d, reason: "" })} data-testid={`reject-draft-${d.id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
                      <Button size="sm" onClick={() => setAccept({ d, map_to: d.new_customer ? "new" : d.customer_id, credit_days: "" })} data-testid={`accept-draft-${d.id}`}><Check className="w-4 h-4 mr-1" /> Accept & invoice</Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

      <Dialog open={!!accept} onOpenChange={(v) => !v && setAccept(null)}>
        <DialogContent className="max-w-md" data-testid="accept-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary" /> Accept submission</DialogTitle></DialogHeader>
          {accept && (
            <div className="space-y-3 text-sm">
              <p>{accept.d.customer_name} · {accept.d.estimate.bags} bags · {money(accept.d.estimate.sales)}{accept.d.cod_paid ? " · cash received (posted as paid)" : " · on credit"}</p>
              {accept.d.new_customer && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Customer record</Label>
                  <Select value={accept.map_to} onValueChange={(v) => setAccept({ ...accept, map_to: v })}>
                    <SelectTrigger data-testid="accept-customer-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Create new customer "{accept.d.customer_name}" (new-customer bonus applies)</SelectItem>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>Map to existing: {c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5"><Label className="text-xs">Credit days (blank = customer default)</Label><Input type="number" value={accept.credit_days} onChange={(e) => setAccept({ ...accept, credit_days: e.target.value })} data-testid="accept-credit-days-input" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={doAccept} disabled={busy} data-testid="confirm-accept-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create invoice"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reject} onOpenChange={(v) => !v && setReject(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject submission</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label className="text-xs">Reason (sent to the agent)</Label><Textarea rows={3} value={reject?.reason || ""} onChange={(e) => setReject({ ...reject, reason: e.target.value })} data-testid="reject-reason-input" /></div>
          <DialogFooter><Button variant="destructive" onClick={doReject} disabled={busy} data-testid="confirm-reject-button">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
